import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import './SubscriptionPage.css';
import config from '../config';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import { calculateDaysRemaining } from '../utils/subscriptionUtils';
import { useSubscription } from '../contexts/SubscriptionContext';

// Plan details and pricing
const PLANS = {
  FREE: {
    id: 'free',
    name: 'Free',
    price: '0.00',
    period: 'month',
    features: [
      'Explore our home models in 3D', 
      'Real plans you can purchase', 
      'Limited selection of design materials', 
      'Sample color palettes'],
  },
  MONTHLY: {
    id: config.paypalPlans.MONTHLY,
    name: 'Pro Plan - Monthly',
    price: '5.99',
    period: 'month',
    features: [
      'Explore our home models in 3D', 
      'Real plans you can purchase', 
      'Expanded Pro catalog of design materials', 
      'Expanded Pro catalog of color palettes', 
      'Save your custom designs to the cloud',
      'Save favorites to the cloud',
      'Sync designs and favorites across all devices'],
  },
  SIX_MONTH: {
    id: config.paypalPlans.SIX_MONTH,
    name: 'Pro Plan - Semi-Annual',
    price: '29.99',
    period: '6-months',
    savings: '33%',
    features: [
      'Explore our home models in 3D', 
      'Real plans you can purchase', 
      'Expanded Pro catalog of design materials', 
      'Expanded Pro catalog of color palettes', 
      'Save your custom designs to the cloud',
      'Save favorites to the cloud',
      'Sync designs and favorites across all devices'],
    },
  ANNUAL: {
    id: config.paypalPlans.ANNUAL,
    name: 'Pro Plan - Annual',
    price: '49.99',
    period: 'year',
    savings: '50%',
    features: [
      'Explore our home models in 3D', 
      'Real plans you can purchase', 
      'Expanded Pro catalog of design materials', 
      'Expanded Pro catalog of color palettes', 
      'Save your custom designs to the cloud',
      'Save favorites to the cloud',
      'Sync designs and favorites across all devices'],
    }
};

const API_BASE_URL = config.backendUrl;

const PAYPAL_FALLBACK_CLIENT_ID = 'AV17PaDC2Bg64P2x2Y37nt5JlnH3GiC1OWOZwED96uOiKNSn5FXx88wvTP3qJ8_sN86hOxl3caCP6Yei';

function SubscriptionPage({ user, unityAuthData, canMakePayments = true }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();
  const paypalContainerRef = useRef(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [searchParams] = useSearchParams();
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [activeProTab, setActiveProTab] = useState('ANNUAL'); // Default to best value plan
  const [paypalRetries, setPaypalRetries] = useState(0);
  const maxPaypalRetries = 3;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Use the subscription context
  const { 
    subscriptionData, 
    loading: subLoading, 
    error: subError, 
    refreshSubscription 
  } = useSubscription();

  // Update local state based on subscription context
  useEffect(() => {
    if (subLoading) {
      setLoading(true);
    } else {
      setCurrentPlan(subscriptionData);
      setLoading(false);
    }
    
    if (subError) {
      setError(subError);
    }
  }, [subscriptionData, subLoading, subError]);

  // Helper function to get Firebase auth token
  const getFirebaseToken = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        console.log('Got Firebase token for authentication');
        return token;
      } catch (error) {
        console.error('Error getting Firebase token:', error);
        return '';
      }
    } else {
      console.warn('No Firebase user is logged in');
      return '';
    }
  };

  // Helper function to get authentication headers with both Firebase and Unity tokens
  const getAuthHeaders = async () => {
    // Get Firebase token
    const auth = getAuth();
    const currentUser = auth.currentUser;
    let firebaseToken = '';
    
    if (currentUser) {
      try {
        // Always force a token refresh to ensure we have a fresh one
        firebaseToken = await currentUser.getIdToken(true);
        console.log('Got fresh Firebase token for API request');
      } catch (error) {
        console.error('Error getting Firebase token:', error);
      }
    } else {
      console.warn('No Firebase user is logged in');
    }
    
    // Only include standard headers, no cache-control to avoid CORS issues
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': firebaseToken ? `Bearer ${firebaseToken}` : '',
      'X-Unity-Token': unityAuthData?.accessToken || ''
    };
    
    return headers;
  };

  // Check for canMakePayments parameter and force refresh if needed
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasCanMakePaymentsParam = searchParams.has('canMakePayments');
    
    if (hasCanMakePaymentsParam && user && unityAuthData) {
      console.log('canMakePayments parameter detected, forcing subscription refresh');
      refreshSubscription();
    }
  }, [window.location.search, user, unityAuthData, refreshSubscription]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if there's a success or cancel parameter in the URL
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    
    if (success === 'true') {
      toast.success('Your subscription has been activated successfully!');
      refreshSubscription();
      // Clear the URL parameter
      navigate('/subscription', { replace: true });
    } else if (cancelled === 'true') {
      toast.info('Subscription process was cancelled.');
      // Clear the URL parameter
      navigate('/subscription', { replace: true });
    }
  }, [searchParams, navigate, refreshSubscription]);

const handleSelectPlan = (planId) => {
    // First check if user has an active subscription
    if (currentPlan && currentPlan.status && 
        ['ACTIVE', 'PAUSED'].includes(currentPlan.status.toUpperCase())) {
        
        // Show error message based on status
        const statusMessage = currentPlan.status.toUpperCase() === 'PAUSED' 
            ? 'You have a paused subscription. Please resume or cancel it before starting a new subscription.'
            : 'You already have an active subscription. Please cancel it before starting a new subscription.';
            
        toast.error(statusMessage);
        return;
    }
    
    console.log(`Selecting plan: ${planId}`);
    console.log('PayPal config:', {
        clientId: config.paypalClientId,
        planId: config.paypalPlans[planId]
    });
    
    setSelectedPlan(planId);
    setIsSubscribing(true);
};

  const handlePauseSubscription = async () => {
    if (!pauseReason) {
      toast.error('Please select a reason for pausing your subscription.');
      return;
    }
    
    try {
      setLoading(true);
      
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/subscription/pause`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          reason: pauseReason,
          playerId: unityAuthData.playerId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to pause subscription');
      }
      
      setShowPauseModal(false);
      setPauseReason('');
      toast.success('Your subscription has been paused successfully.');
      
      // Refresh subscription status
      await refreshSubscription();
    } catch (error) {
      console.error('Error pausing subscription:', error);
      toast.error('Failed to pause subscription. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setLoading(true);
      
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/subscription/${currentPlan.subscriptionId}/resume`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          playerId: unityAuthData.playerId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to resume subscription');
      }
      
      toast.success('Your subscription has been resumed successfully.');
      
      // Refresh subscription status
      await refreshSubscription();
    } catch (error) {
      console.error('Error resuming subscription:', error);
      toast.error('Failed to resume subscription. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelReason && showCancelModal) {
      toast.error('Please select a reason for cancelling your subscription.');
      return;
    }
    
    try {
      setLoading(true);
      
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          reason: cancelReason,
          playerId: unityAuthData?.playerId,
          subscriptionId: currentPlan?.subscriptionId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API error response:', errorData);
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      
      const responseData = await response.json();
      console.log('Cancel subscription response:', responseData);
      
      setShowCancelModal(false);
      setCancelReason('');
      toast.success('Your subscription has been cancelled successfully. You can still use premium features until the end of your billing period.');
      
      await refreshSubscription();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(`Failed to cancel subscription: ${error.message}`);
      
      setShowCancelModal(false);
      setCancelReason('');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async (data, actions) => {
    const planId = (() => {
      switch (selectedPlan) {
        case 'MONTHLY':
          return PLANS.MONTHLY.id;
        case 'SIX_MONTH':
          return PLANS.SIX_MONTH.id;
        case 'ANNUAL':
          return PLANS.ANNUAL.id;
        default:
          return PLANS.MONTHLY.id;
      }
    })();
    
    try {
      return actions.subscription.create({
        plan_id: planId
      });
    } catch (err) {
      console.error('PayPal subscription creation error:', err);
      toast.error('An error occurred while setting up your subscription. Please try again.');
      return null;
    }
  };

  const handleApproveSubscription = async (data, actions) => {
    try {
      const subscriptionId = data.subscriptionID;
      
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/subscription/activate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          plan: selectedPlan,
          playerId: unityAuthData.playerId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to activate subscription');
      }
      console.log(response)
      toast.success('Your subscription has been activated successfully!');
      setIsPaymentSuccess(true);
      setIsSubscribing(false);
      
      await refreshSubscription();
    } catch (err) {
      console.error('Error activating subscription:', err);
      toast.error('Failed to activate your subscription. If you were charged, please contact support.');
    }
  };

  const handlePayPalError = (err) => {
    console.error('PayPal error:', err);
    toast.error('An error occurred with the payment process. Please try again later.');
  };

  const handleCancel = () => {
    setIsSubscribing(false);
    setSelectedPlan(null);
  };

  const getPayPalOptions = () => {
    return {
      "client-id": config.paypalClientId || PAYPAL_FALLBACK_CLIENT_ID,
      components: "buttons",
      intent: "subscription",
      "enable-funding": "paypal",
      "disable-funding": "credit,card",
      "data-client-token": config.paypalClientToken || undefined,
      currency: "USD",
      vault: true,
      commit: true
    };
  };

  // Fallback payment method
  const handleFallbackPayment = () => {
    const plan = PLANS[selectedPlan];
    const subscriptionId = `manual-${Date.now()}`;
    
    setIsSubscribing(true);
    setError(null);
    
    setTimeout(async () => {
      try {
        const token = await getFirebaseToken();
        if (!token) {
          throw new Error('Authentication failed. Please try again later.');
        }
        
        const response = await fetch(`${API_BASE_URL}/subscription/direct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            playerId: user?.uid,
            planId: plan.id,
            subscriptionId: subscriptionId,
            provider: 'MANUAL'
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          toast.success('Your subscription has been activated!');
          setCurrentPlan({
            plan: selectedPlan,
            status: 'ACTIVE',
            expiresAt: data.expiresAt
          });
          setIsSubscribing(false);
          setSelectedPlan(null);
        } else {
          setError('Failed to activate your subscription. Please try again later.');
          setIsSubscribing(false);
        }
      } catch (err) {
        console.error('Fallback payment error:', err);
        setError('An error occurred. Please try again later.');
        setIsSubscribing(false);
      }
    }, 2000);
  };

  // Render a plan card
  const renderPlanCard = (planKey) => {
    const plan = PLANS[planKey];
    const isCurrent = currentPlan?.plan === planKey;
    const isActive = currentPlan?.status === 'ACTIVE' && isCurrent;
    const isPaused = currentPlan?.status === 'PAUSED' && isCurrent;
    const isCancelled = currentPlan?.status === 'CANCELLED' && isCurrent;

    const handleCardClick = () => {
      if (!isCurrent && canMakePayments && !isSubscribing) {
        handleSelectPlan(planKey);
      }
    };

    return (
      <div 
        className={`relative plan-card bg-white p-6 rounded-lg shadow-md transition-all duration-300 w-full max-w-xs mx-auto ${ 
          isCurrent 
            ? 'border-2 border-indigo-600 ring-2 ring-offset-2 ring-indigo-300 shadow-xl' 
            : 'hover:shadow-lg hover:transform hover:scale-105'
        } ${!isCurrent && canMakePayments && !isSubscribing ? 'cursor-pointer' : ''}`}
        onClick={handleCardClick}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        tabIndex={!isCurrent && canMakePayments && !isSubscribing ? 0 : -1}
        aria-label={!isCurrent ? `Select ${plan.name} plan` : `Your current plan: ${plan.name}`}
        role={!isCurrent && canMakePayments && !isSubscribing ? "button" : undefined}
      >
        {/* Enhanced Current Plan Badge */}
        {isCurrent && <div className="plan-badge">Current Plan</div>}
        {plan.badge && !isCurrent && <div className="plan-badge">{plan.badge}</div>}

        <div className="plan-header">
          <h3>{plan.name}</h3>
          <div className="plan-price">
            <span className="price-amount">${plan.price}</span>
            <span className="price-period">/ {plan.period}</span>
          </div>
          {plan.savings && (
            <div className="savings-text">
              ({plan.savings} off)
            </div>
          )}
        </div>
        
        <ul className="plan-features">
          {plan.features.map((feature, index) => (
            <li key={index} className="feature-item">
              <div className="feature-content">
                <span className="feature-icon">✓</span>
                <span className="feature-text">{feature}</span>
              </div>
            </li>
          ))}
        </ul>
        
        <div className="plan-cta">
          {isCurrent ? (
            <div className="current-plan-info">
              {isPaused && (
                <div className="paused-plan-message">
                  <p>Subscription paused.</p>
                  {currentPlan.pauseUntil && (
                    <p>Resumes: {formatDistanceToNow(new Date(currentPlan.pauseUntil), { addSuffix: true })}</p>
                  )}
                  <button 
                    className="resume-button plan-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeSubscription();
                    }}
                    aria-label="Resume subscription"
                  >
                    Resume Now
                  </button>
                </div>
              )}
              
              {isCancelled && (
                <div className="cancelled-plan-info">
                  <p>Subscription cancelled.</p>
                  {currentPlan.expiresAt && (
                    <p>Expires in: {formatDistanceToNow(new Date(currentPlan.expiresAt), { addSuffix: true })}</p>
                  )}
                  <button 
                    className="renew-button plan-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPlan(planKey);
                    }}
                    aria-label="Renew subscription"
                  >
                    Renew
                  </button>
                </div>
              )}
              
              {isActive && (
                <div className="active-plan-info">
                  {currentPlan.expiresAt && (
                    <p>Renews {formatDistanceToNow(new Date(currentPlan.expiresAt), { addSuffix: true })}</p>
                  )}
                  <div className="manage-subscription">
                    {/*<button */}
                    {/*  className="pause-button plan-button"*/}
                    {/*  onClick={(e) => {*/}
                    {/*    e.stopPropagation();*/}
                    {/*    setShowPauseModal(true);*/}
                    {/*  }}*/}
                    {/*  aria-label="Pause subscription"*/}
                    {/*>*/}
                    {/*  Pause*/}
                    {/*</button>*/}
                    {(currentPlan.subscriptionSource !== 'apple') && (

                        <button 
                      className="cancel-sub-button plan-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCancelModal(true);
                      }}
                      aria-label="Cancel subscription"
                    >
                      Cancel
                    </button>
                        )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderFreePlanCard = () => {
    const isCurrentPlan = !currentPlan || currentPlan.plan === PLANS.FREE.id;
    
    const handleCardClick = () => {
      if (!isCurrentPlan) {
        handleCancelSubscription();
      }
    };
    
    return (
      <div 
        className={`relative plan-card bg-white p-6 rounded-lg shadow-md transition-all duration-300 w-full max-w-xs mx-auto ${
          isCurrentPlan 
            ? 'border-2 border-indigo-600 ring-2 ring-offset-2 ring-indigo-300 shadow-xl' 
            : 'hover:shadow-lg hover:transform hover:scale-105'
        } ${!isCurrentPlan ? 'cursor-pointer' : ''}`}
        onClick={!isCurrentPlan ? handleCardClick : undefined}
        onKeyDown={(e) => !isCurrentPlan && e.key === 'Enter' && handleCardClick()}
        tabIndex={!isCurrentPlan ? 0 : -1}
        aria-label={!isCurrentPlan ? "Downgrade to free plan" : "Your current free plan"}
        role={!isCurrentPlan ? "button" : undefined}
      >
        {/* Enhanced Current Plan Badge */}
        {isCurrentPlan && <div className="absolute top-2 right-2 bg-indigo-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow">Current Plan</div>}
        
        <div className="plan-header">
          <h3>{PLANS.FREE.name} Plan</h3>
          <div className="plan-price">
            <span className="price-amount">${PLANS.FREE.price}</span>
            <span className="price-period">/ month</span>
          </div>
        </div>
        
        <ul className="plan-features">
          {PLANS.FREE.features.map((feature, index) => (
            <li key={index} className="feature-item">
              <div className="feature-content">
                <span className="feature-icon">✓</span>
                <span className="feature-text">{feature}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Render payment section
  const renderPaymentSection = () => {
    const plan = PLANS[selectedPlan];
    
    return (
      <div className="payment-section">
        <h3>Complete Your Subscription</h3>
        <div className="selected-plan-info">
          <p><strong>{plan.name}</strong> - ${plan.price}</p>
        </div>
        
        {error && (
          <div className="payment-error">
            {error}
          </div>
        )}
        
        <div className="paypal-container" ref={paypalContainerRef}>
          <PayPalScriptProvider options={getPayPalOptions()}
          onError={(err) => {
            console.error('PayPal SDK loading error:', err);
            setError('Failed to load payment provider. Please try again later.');
          }}>
            {error ? (
              <div className="payment-fallback">
                <p>We're experiencing issues with our payment processor. Please try again later or contact support.</p>
                <p>You can also try using a different browser or clearing your browser cookies.</p>
                <button 
                  className="fallback-button"
                  onClick={handleFallbackPayment}
                  aria-label="Use fallback payment method"
                >
                  Use Fallback Payment Method
                </button>
              </div>
            ) : (
              <PayPalButtons
                style={{
                  shape: 'rect',
                  color: 'blue',
                  layout: 'vertical',
                  label: 'subscribe'
                }}
                forceReRender={[plan.id]}
                createSubscription={handleCreateSubscription}
                onApprove={handleApproveSubscription}
                onError={handlePayPalError}
                onCancel={handleCancel}
              />
            )}
          </PayPalScriptProvider>
        </div>
        
        <div className="payment-debug-info">
          <small>If you're having trouble with payments, please ensure you have cookies enabled and try a different browser.</small>
        </div>
        
        <button 
          className="cancel-button"
          onClick={handleCancel}
          aria-label="Cancel payment"
        >
          Cancel
        </button>
      </div>
    );
  };

  const renderSubscriptionContent = () => {
    if (error) {
      return <div className="error-message">{error}</div>;
    }

    if (isSubscribing && selectedPlan) {
      return renderPaymentSection();
    }

    const allPlanKeys = ['FREE', 'MONTHLY', 'SIX_MONTH', 'ANNUAL'];
    
    let orderedPlanKeys = [...allPlanKeys];
    if (currentPlan && currentPlan.plan) {
      orderedPlanKeys = orderedPlanKeys.filter(plan => plan !== currentPlan.plan);
      orderedPlanKeys.unshift(currentPlan.plan);
    }

    return (
      <div>
        <div className="pro-header">
          <h2>Choose Your Plan</h2>
        </div>
        
        <div className="plans-container">
          <div className="pro-plans-container">
            
            <div className="all-plans-container">
              {orderedPlanKeys
                .filter(planKey => planKey !== 'FREE' || (!currentPlan || currentPlan.plan === 'FREE'))
                .map(planKey => (
                  <div key={planKey} className="plan-item">
                    {planKey === 'FREE' ? renderFreePlanCard() : renderPlanCard(planKey)}
                  </div>
                ))}
            </div>
          </div>
        </div>
        
        {isPaymentSuccess && (
          <div className="success-message">
            <p>Thank you for subscribing! Your premium features are now active.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="subscription-page">
      <div className="subscription-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading subscription information...</p>
          </div>
        ) : (
          renderSubscriptionContent()
        )}
      </div>
      
      {showPauseModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Pause Your Subscription</h3>
            <p>Please let us know why you're pausing your subscription:</p>
            <select 
              value={pauseReason} 
              onChange={(e) => setPauseReason(e.target.value)}
              aria-label="Reason for pausing subscription"
            >
              <option value="">Select a reason</option>
              <option value="temporarily_not_using">Temporarily not using the app</option>
              <option value="financial_reasons">Financial reasons</option>
              <option value="trying_other_services">Trying other services</option>
              <option value="missing_features">Missing features I need</option>
              <option value="other">Other reason</option>
            </select>
            <div className="modal-buttons">
              <button 
                className="cancel-modal-button"
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseReason('');
                }}
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button 
                className="confirm-modal-button"
                onClick={handlePauseSubscription}
                disabled={!pauseReason}
                aria-label="Confirm pause"
              >
                Confirm Pause
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Cancel Your Subscription</h3>
            <p>We're sorry to see you go. Please let us know why you're cancelling:</p>
            <select 
              value={cancelReason} 
              onChange={(e) => setCancelReason(e.target.value)}
              aria-label="Reason for cancelling subscription"
            >
              <option value="">Select a reason</option>
              <option value="too_expensive">Too expensive</option>
              <option value="not_using_enough">Not using the app enough</option>
              <option value="missing_features">Missing features I need</option>
              <option value="poor_experience">Poor user experience</option>
              <option value="other">Other reason</option>
            </select>
            <div className="modal-buttons">
              <button 
                className="cancel-modal-button"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                aria-label="Go back"
              >
                Go Back
              </button>
              <button 
                className="confirm-modal-button"
                onClick={handleCancelSubscription}
                disabled={!cancelReason}
                aria-label="Confirm cancellation"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}
      
     
    </div>
  );
}

export default SubscriptionPage;